import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/common/Badge';
import { Card } from '@/components/common/Card';
import { ErrorState, LoadingState } from '@/components/common/States';
import { TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { employeeAttendanceApi } from '@/services/api/endpoints/attendance';
import { employeesApi } from '@/services/api/endpoints/staff';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { EmployeeAttendanceStatus } from '@/types/domain';
import { humanise } from '@/utils/format';

/*
 * ON_LEAVE and HOLIDAY are absent from the picker on purpose: the first is
 * written by approving a leave request and the second by the calendar, and
 * letting a clerk set either by hand would put attendance out of step with the
 * record that justifies it.
 */
const STATUSES: EmployeeAttendanceStatus[] = ['PRESENT', 'ABSENT', 'LATE', 'HALF_DAY', 'REMOTE'];

const TONE: Record<EmployeeAttendanceStatus, 'success' | 'danger' | 'warning' | 'neutral' | 'info'> = {
  PRESENT: 'success',
  ABSENT: 'danger',
  LATE: 'warning',
  ON_LEAVE: 'info',
  HALF_DAY: 'neutral',
  REMOTE: 'info',
  HOLIDAY: 'neutral',
};

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Staff attendance for one day.
 *
 * Recorded against the employee rather than the teacher, so a bursar or a
 * caretaker is recordable too. One record per person per day: marking again
 * corrects the existing row instead of failing on the unique constraint.
 */
export function StaffAttendancePage() {
  const { can } = useAuth();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [date, setDate] = useState(today());

  const employees = useQuery({
    queryKey: queryKeys.employees.list({ size: 200, status: 'ACTIVE' }),
    queryFn: () => employeesApi.list({ page: 0, size: 200, status: 'ACTIVE' }),
  });

  const attendance = useQuery({
    queryKey: queryKeys.attendance.teacherDay(date),
    queryFn: () => employeeAttendanceApi.forDay(date),
  });

  const record = useMutation({
    mutationFn: ({ employeeId, status }: { employeeId: string; status: EmployeeAttendanceStatus }) =>
      employeeAttendanceApi.record({ employeeId, attendanceDate: date, status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.attendance.all });
    },
    onError: (error) => handleApiError(error),
  });

  /** What is already recorded today, keyed for lookup against the staff list. */
  const recorded = new Map(
    (attendance.data ?? []).map((entry) => [entry.employeeId, entry.status] as const),
  );

  const markedCount = recorded.size;
  const staff = employees.data?.data ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Staff attendance"
        description="Everyone the school employs, not only teaching staff."
      />

      <Card className="p-5">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div className="max-w-xs flex-1">
            <TextField
              label="Date"
              type="date"
              value={date}
              max={today()}
              onChange={(event) => setDate(event.target.value)}
            />
          </div>
          <p className="text-sm text-muted">
            {markedCount} of {staff.length} recorded
          </p>
        </div>

        {employees.isPending || attendance.isPending ? (
          <LoadingState label="Loading staff" />
        ) : employees.error !== null ? (
          <ErrorState error={employees.error} onRetry={() => void employees.refetch()} />
        ) : staff.length === 0 ? (
          <p className="py-6 text-center text-sm text-subtle">No active employees.</p>
        ) : (
          <ul className="divide-y divide-line">
            {staff.map((employee) => {
              const status = recorded.get(employee.id);
              return (
                <li
                  key={employee.id}
                  className="flex flex-wrap items-center justify-between gap-3 py-2.5"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm text-ink">
                      {employee.fullName ?? employee.employeeNumber}
                    </p>
                    <p className="font-mono text-xs text-subtle">{employee.employeeNumber}</p>
                  </div>

                  {can(PERMISSIONS.EMPLOYEE_ATTENDANCE_RECORD) ? (
                    <div className="flex flex-wrap gap-1" role="group">
                      {STATUSES.map((option) => (
                        <button
                          key={option}
                          type="button"
                          aria-pressed={status === option}
                          onClick={() => record.mutate({ employeeId: employee.id, status: option })}
                          className={`rounded-md border px-2.5 py-1 text-xs transition-colors ${
                            status === option
                              ? 'border-accent bg-accent-soft text-accent-soft-fg'
                              : 'border-line text-subtle hover:border-line-strong hover:text-muted'
                          }`}
                        >
                          {humanise(option)}
                        </button>
                      ))}
                    </div>
                  ) : status === undefined ? (
                    <Badge tone="neutral">Not recorded</Badge>
                  ) : (
                    <Badge tone={TONE[status]}>{humanise(status)}</Badge>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}
